// Resolution of the notabox-server base URL.
//
// This lives outside App.js because the portal session layer has to reach
// /api/v1/auth/* before the dashboard mounts — the login screen renders
// instead of App's tree, not inside it.
//
// Three env vars, each overridable at runtime via window.REACT_APP_* so one
// image can target different deployments by injecting them into index.html.

const API_BASE =
  (typeof window !== "undefined" && window.REACT_APP_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  "http://localhost:8090";

export const API_ROUTE =
  (typeof window !== "undefined" && window.REACT_APP_API_ROUTE) ||
  process.env.REACT_APP_API_ROUTE ||
  "";

const PROXY_BASE =
  (typeof window !== "undefined" && window.REACT_APP_PROXY_BASE) ||
  process.env.REACT_APP_PROXY_BASE ||
  "http://localhost:11095";

export const resolveApiBase = () => {
  if (typeof window === "undefined") return API_BASE;
  if (!API_BASE || API_BASE === "same-origin") return window.location.origin;
  return API_BASE;
};

export const resolveProxyBase = () => {
  if (typeof window === "undefined") return PROXY_BASE;
  return window.location.origin;
};

export const joinUrl = (base, path) => {
  if (!base) return path;
  if (base.endsWith("/") && path.startsWith("/")) return base.slice(0, -1) + path;
  if (!base.endsWith("/") && !path.startsWith("/")) return `${base}/${path}`;
  return base + path;
};

// serverApiUrl maps a notabox-server path (/nodes, /auth/login, …) to a URL.
// In the default proxy deployment that is a same-origin /internal/api/v1 path,
// which is what keeps the session cookie attached to these calls.
export const serverApiUrl = (path) => {
  const proxyBase = resolveProxyBase();
  if (proxyBase) return joinUrl(proxyBase, `/internal/api/v1${path}`);
  return `${resolveApiBase()}${API_ROUTE}${path}`;
};

const SINGLE_EDGE_RAW =
  (typeof window !== "undefined" && window.REACT_APP_SINGLE_EDGE) ||
  process.env.REACT_APP_SINGLE_EDGE ||
  "";

// IS_SINGLE_EDGE collapses the UI to one device — the on-device console build
// (cloud-anchor/pkg/consoleui) serves it this way.
export const IS_SINGLE_EDGE =
  String(SINGLE_EDGE_RAW) === "1" || String(SINGLE_EDGE_RAW).toLowerCase() === "true";

export const CONSOLE_API_PREFIX = "/console/api";

// Top-level view routing lives in appView.js — this module stays about API
// URL resolution only.

// nodeApiUrlFor builds a per-edge API URL resolver. It lives here rather than
// in App.js because the assistant renders as its own window, outside App's
// tree, and both need the identical resolution — a second copy would drift.
export const nodeApiUrlFor = (nodeId) => (path) => {
  if (IS_SINGLE_EDGE) {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}${CONSOLE_API_PREFIX}${path}`;
  }
  if (!nodeId) return null;
  const encoded = encodeURIComponent(nodeId);
  const proxyBase = resolveProxyBase();
  if (proxyBase) return joinUrl(proxyBase, `/node/${encoded}${path}`);
  return serverApiUrl(`/nodes/${encoded}/proxy${path}`);
};
